import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Grid,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

const CONTRACT_LABELS = {
  OwnershipContract: 'Eigentum',
  LeaseContract: 'Miete',
  Vacancy: 'Leerstand',
}

const ROLE_LABELS = {
  owner: 'Eigentümer',
  tenant: 'Mieter',
  guarantor: 'Bürge',
  representative: 'Vertreter',
}

Show.layout = (page) => <AppShell>{page}</AppShell>

export default function Show({ property, contract }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [tabIndex, setTabIndex] = useState(0)

  const basePath = `/m/${tenant.slug}/properties/${property.id}/contracts`

  return (
    <Box>
      <PageHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{contract.number || `Vertrag #${contract.id}`}</span>
            <Chip
              label={CONTRACT_LABELS[contract.type] || contract.type}
              color="primary"
              size="small"
              variant="outlined"
            />
            <Chip
              label={contract.active ? 'Aktiv' : 'Beendet'}
              color={contract.active ? 'success' : 'default'}
              size="small"
            />
          </Box>
        }
        breadcrumbs={[
          { label: 'Verträge', href: basePath },
          { label: contract.number || `#${contract.id}` },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {contract.type === 'OwnershipContract' && contract.active && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<SwapHorizIcon />}
                onClick={() => router.visit(`${basePath}/${contract.id}/ownership_change`)}
                data-testid="ownership-change-btn"
              >
                Eigentümerwechsel
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => router.visit(`${basePath}/${contract.id}/edit`)}
              data-testid="edit-contract-btn"
            >
              Bearbeiten
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                if (window.confirm('Vertrag wirklich löschen?')) {
                  router.delete(`${basePath}/${contract.id}`)
                }
              }}
              data-testid="delete-contract-btn"
            >
              Löschen
            </Button>
          </Box>
        }
      />

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Stammdaten" data-testid="tab-stammdaten" />
          <Tab label="Kontakte" data-testid="tab-kontakte" />
          <Tab label="Sollstellungen" data-testid="tab-receivables" />
          <Tab label="Zahlungen" data-testid="tab-payments" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabIndex === 0 && (
            <Grid container spacing={2} sx={{ maxWidth: 600 }}>
              <Grid size={{ xs: 12 }}>
                <DetailRow label="Vertragsnummer" value={contract.number || '–'} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <DetailRow label="Typ" value={CONTRACT_LABELS[contract.type] || contract.type} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <DetailRow label="Einheit" value={contract.unit_number} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <DetailRow label="Beginn" value={formatDate(contract.start_date)} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <DetailRow label="Ende" value={formatDate(contract.end_date) || '–'} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <DetailRow label="Kündigungsfrist" value={contract.notice_period || '–'} />
              </Grid>
            </Grid>
          )}

          {tabIndex === 1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Rang</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rolle</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!contract.contract_contacts || contract.contract_contacts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">Keine Kontakte zugeordnet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(contract.contract_contacts || []).map((cc) => (
                    <TableRow key={cc.id} data-testid={`contact-row-${cc.id}`}>
                      <TableCell>{cc.rank}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{cc.contact_name}</Typography>
                      </TableCell>
                      <TableCell>{ROLE_LABELS[cc.role] || cc.role}</TableCell>
                      <TableCell>
                        <Chip label={cc.contact_type === 'Person' ? 'Person' : 'Unternehmen'} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tabIndex === 2 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Sollstellungen werden in einem zukünftigen Update verfügbar sein.</Typography>
            </Box>
          )}

          {tabIndex === 3 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Zahlungen werden in einem zukünftigen Update verfügbar sein.</Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function formatDate(dateStr) {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (isNaN(d)) return dateStr
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
